// components/NewMeetingModal.jsx

import { useState, useRef } from 'react';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function getUserId() {
  return window.__clerk_user_id || 'default';
}

// Upload with progress using XMLHttpRequest
function uploadWithProgress(file, title, emails, onProgress) {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append('audio', file);
    if (title) form.append('title', title);
    if (emails.length) form.append('emails', emails.join(','));

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE}/api/meetings/upload`);
    xhr.setRequestHeader('x-user-id', getUserId());

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 400) {
          const err = new Error(data.message || data.error || `HTTP ${xhr.status}`);
          err.code = data.error;
          err.status = xhr.status;
          reject(err);
        } else {
          resolve(data);
        }
      } catch (e) { reject(new Error('Invalid response')); }
    };

    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(form);
  });
}

export default function NewMeetingModal({ defaultMode = 'file', onCreated, onClose, onUpgrade }) {
  const [mode, setMode] = useState(defaultMode);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [transcript, setTranscript] = useState('');
  const [emails, setEmails] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [stage, setStage] = useState(''); // 'uploading' | 'processing'
  const [error, setError] = useState('');
  const [limitReached, setLimitReached] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef();

  const handleFile = (f) => {
    if (!f) return;
    const sizeMB = f.size / (1024 * 1024);
    if (sizeMB > 50) {
      setError(`File is ${sizeMB.toFixed(1)}MB — maximum allowed is 50MB. Please trim or compress your recording first.`);
      return;
    }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' '));
    setError('');
  };

  const handleSubmit = async () => {
    setError(''); setLimitReached(false); setLoading(true);
    try {
      const emailList = emails.split(',').map(e => e.trim()).filter(Boolean);
      let result;

      if (mode === 'file') {
        if (!file) throw new Error('Please select a file');
        setStage('uploading');
        setUploadProgress(0);
        result = await uploadWithProgress(file, title, emailList, (pct) => {
          setUploadProgress(pct);
          if (pct === 100) setStage('processing');
        });
      } else {
        if (!transcript.trim()) throw new Error('Please paste a transcript');
        setStage('processing');
        const res = await fetch(`${BASE}/api/meetings/transcript`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-user-id': getUserId() },
          body: JSON.stringify({ transcript, title, emails: emailList }),
        });
        const data = await res.json();
        if (!res.ok) {
          const err = new Error(data.message || data.error || `HTTP ${res.status}`);
          err.code = data.error;
          err.status = res.status;
          throw err;
        }
        result = data;
      }

      onCreated(result.meeting_id);
      onClose();
    } catch (e) {
      if (e.code === 'upgrade_required' || e.status === 403) {
        setLimitReached(true);
      } else {
        setError(e.message);
      }
    } finally {
      setLoading(false);
      setStage('');
      setUploadProgress(0);
    }
  };

  const fileSizeMB = file ? (file.size / (1024 * 1024)).toFixed(1) : 0;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">New Meeting Analysis</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>

        {/* ── Upgrade wall ── */}
        {limitReached ? (
          <div style={{ padding: '2rem 1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.25rem', textAlign: 'center' }}>
            <div style={{ fontSize: 40 }}>🔒</div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>Free plan limit reached</div>
              <div style={{ fontSize: 13, color: 'var(--text-3)', lineHeight: 1.6 }}>
                You've used your 2 free meetings this month.<br />
                Upgrade to Pro for unlimited meetings, PDF export, Zoom import and more.
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', width: '100%' }}>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={onClose}>Maybe later</button>
              <button className="btn btn-primary" style={{ flex: 2 }} onClick={() => { onClose(); onUpgrade?.(); }}>
                Upgrade to Pro →
              </button>
            </div>
          </div>

        ) : loading ? (
          /* ── Progress view ── */
          <div style={{ padding: '2rem 1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem', textAlign: 'center' }}>
            <div style={{ fontSize: 40 }}>
              {stage === 'uploading' ? '📤' : '⚙️'}
            </div>

            <div>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: 'var(--text)' }}>
                {stage === 'uploading' ? 'Uploading your recording...' : 'Processing meeting...'}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
                {stage === 'uploading'
                  ? `${fileSizeMB}MB · ${uploadProgress}% uploaded`
                  : 'Compressing → Transcribing → Analysing'}
              </div>
            </div>

            {/* Upload progress bar */}
            {stage === 'uploading' && (
              <div style={{ width: '100%' }}>
                <div style={{
                  height: 6, background: 'var(--bg-3)', borderRadius: 99,
                  overflow: 'hidden', width: '100%',
                }}>
                  <div style={{
                    height: '100%',
                    width: `${uploadProgress}%`,
                    background: 'var(--accent)',
                    borderRadius: 99,
                    transition: 'width 0.3s ease',
                    boxShadow: '0 0 8px rgba(0,255,136,0.4)',
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>
                  <span>{uploadProgress}%</span>
                  <span>{fileSizeMB}MB</span>
                </div>
              </div>
            )}

            {/* Processing steps */}
            {stage === 'processing' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
                {[
                  { label: 'Compressing audio', icon: '🗜️' },
                  { label: 'Transcribing with Deepgram', icon: '🎙️' },
                  { label: 'Analysing with AI', icon: '🧠' },
                  { label: 'Extracting action items', icon: '⚡' },
                ].map((step, i) => (
                  <div key={step.label} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 14px',
                    background: 'var(--bg-3)',
                    borderRadius: 8,
                    border: '1px solid var(--border)',
                    fontSize: 13,
                    color: 'var(--text-2)',
                    animation: `fadeIn 0.4s ease ${i * 0.15}s both`,
                  }}>
                    <span>{step.icon}</span>
                    <span>{step.label}</span>
                    <div style={{ marginLeft: 'auto' }}>
                      <div className="processing-bar" style={{ width: 40, height: 3 }}>
                        <div className="processing-bar-fill" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ fontSize: 12, color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>
              {stage === 'uploading' ? 'Do not close this window' : 'This may take a minute for long recordings'}
            </div>
          </div>

        ) : (
          <>
            <div className="modal-body">
              {/* Mode toggle */}
              <div style={{ display: 'flex', background: 'var(--bg-3)', borderRadius: 'var(--radius)', padding: 3, marginBottom: '1.25rem' }}>
                {[['file', '↑ Upload File'], ['transcript', '✎ Paste Transcript']].map(([m, label]) => (
                  <button key={m} onClick={() => setMode(m)} style={{
                    flex: 1, padding: '0.45rem', borderRadius: 4, border: 'none',
                    background: mode === m ? 'var(--bg-2)' : 'transparent',
                    color: mode === m ? 'var(--text)' : 'var(--text-3)',
                    fontWeight: mode === m ? 600 : 400,
                    fontSize: 13, cursor: 'pointer',
                    boxShadow: mode === m ? 'var(--shadow)' : 'none',
                    transition: 'all 0.15s',
                  }}>{label}</button>
                ))}
              </div>

              {/* Title */}
              <div style={{ marginBottom: '1rem' }}>
                <label className="text-xs text-dim text-mono" style={{ display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Title</label>
                <input type="text" placeholder="Q4 Planning, Sprint Review, Design Critique..." value={title} onChange={e => setTitle(e.target.value)} />
              </div>

              {/* File upload */}
              {mode === 'file' && (
                <div style={{ marginBottom: '1rem' }}>
                  <div
                    className={`upload-zone ${dragging ? 'drag-over' : ''}`}
                    onDragOver={e => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
                    onClick={() => fileRef.current?.click()}
                  >
                    <input ref={fileRef} type="file" accept="audio/*,video/*" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
                    <div style={{ fontSize: 28, marginBottom: '0.75rem' }}>{file ? '🎙️' : '📁'}</div>
                    {file ? (
                      <>
                        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{file.name}</div>
                        <div className="text-sm text-dim">{fileSizeMB}MB · Click to change</div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Drop audio or video file</div>
                        <div className="text-sm text-dim">MP3, MP4, WAV, M4A, WEBM · Max 50MB</div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Transcript */}
              {mode === 'transcript' && (
                <div style={{ marginBottom: '1rem' }}>
                  <label className="text-xs text-dim text-mono" style={{ display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Transcript</label>
                  <textarea
                    rows={9}
                    placeholder="Paste meeting transcript here — from Zoom, Google Meet, Otter.ai, or any other source..."
                    value={transcript}
                    onChange={e => setTranscript(e.target.value)}
                    style={{ resize: 'vertical' }}
                  />
                  {transcript && <div className="text-xs text-dim mt-xs">~{Math.round(transcript.split(/\s+/).length)} words</div>}
                </div>
              )}

              {/* Emails */}
              <div>
                <label className="text-xs text-dim text-mono" style={{ display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Send summary to (optional)
                </label>
                <input type="text" placeholder="alice@co.com, bob@co.com" value={emails} onChange={e => setEmails(e.target.value)} />
                <div className="text-xs text-dim mt-xs">Comma separated</div>
              </div>

              {error && (
                <div style={{ marginTop: '1rem', padding: '0.65rem 0.75rem', background: 'var(--red-dim)', border: '1px solid rgba(255,68,85,0.3)', borderRadius: 'var(--radius)', color: 'var(--red)', fontSize: 13 }}>
                  ⚠ {error}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={onClose}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={loading || (mode === 'file' ? !file : !transcript.trim())}
              >
                → Analyse Meeting
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
